import React, { useState } from 'react';
import { ShowBase, EditButton, DeleteButton, Loading, useRecordContext, useTranslate, useNotify, useRefresh } from 'react-admin';
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip, Legend } from 'recharts';
import { useTheme, alpha } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MuiTooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { postReviewAction, putReviewActionPlan } from '../../services/api';
import useReviewGrid, { axesByBlock, levelLabel, targetFor } from './useReviewGrid';
import { GapChip, kindLabel, statusLabel } from './ReviewForm';
import Pill from './Pill';

const ACTIONS = [
    { key: 'submit', label: 'Submit', confirm: 'Once submitted, your self review can no longer be changed.' },
    { key: 'share', label: 'Share with the person', confirm: 'The person will be able to read this review.' },
    { key: 'finalize', label: 'Finalize', confirm: 'A finalized review is locked for good.' },
];

const STATUS_COLORS = { draft: 'default', submitted: 'info', shared: 'primary', final: 'success' };

// One color per level, from "not there yet" to "lifts the company".
const useLevelColors = () => {
    const theme = useTheme();
    return {
        1: theme.palette.error.main,
        2: theme.palette.warning.main,
        3: theme.palette.success.main,
        4: theme.palette.primary.main,
    };
};

const cardSx = { borderRadius: '12px', height: '100%' };

const ReviewActions = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);

    const run = async (action) => {
        if (!window.confirm(translate(action.confirm))) {
            return;
        }
        setLoading(true);
        try {
            await postReviewAction(record.id, action.key);
            refresh();
        } catch (error) {
            notify(error.message, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {ACTIONS.filter(action => record.actions?.[action.key]).map(action => (
                <Button key={action.key} variant="contained" disabled={loading} onClick={() => run(action)}>
                    {translate(action.label)}
                </Button>
            ))}
            {record.actions?.edit && <EditButton />}
            {record.actions?.delete && <DeleteButton mutationMode="pessimistic" />}
        </Box>
    );
};

const initials = (fullname = '') => fullname.split(' ').filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();

// The stages as a path: the current one filled, the target one dashed.
const StagePath = ({ grid, current, target }) => {
    const translate = useTranslate();

    return (
        <Box>
            <Typography variant="overline" color="text.secondary">{translate('Stage')}</Typography>
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                {grid.stages.map((stage, index) => {
                    const isCurrent = stage.key === current;
                    const isTarget = stage.key === target && target !== current;
                    return (
                        <React.Fragment key={stage.key}>
                            {index > 0 && <ArrowForwardIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
                            <Pill
                                label={stage.label}
                                color={isCurrent || isTarget ? 'primary' : 'default'}
                                variant={isCurrent ? 'filled' : 'outlined'}
                                dashed={isTarget}
                            />
                        </React.Fragment>
                    );
                })}
            </Box>
            {target && target !== current && (
                <Typography variant="caption" color="text.secondary">{translate('Dashed: target stage')}</Typography>
            )}
        </Box>
    );
};

const Header = ({ grid }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const theme = useTheme();
    const period = [record.period_label, record.period_start && `${record.period_start} → ${record.period_end || ''}`]
        .filter(Boolean).join(' · ');

    return (
        <Card variant="outlined" sx={{ borderRadius: '12px', marginBottom: '16px', background: alpha(theme.palette.primary.main, 0.04) }}>
            <CardContent sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <Avatar sx={{ width: 64, height: 64, fontSize: 24, bgcolor: 'primary.main' }}>{initials(record.user?.fullname)}</Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 240 }}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>{record.user?.fullname}</Typography>
                        <Pill label={statusLabel(translate, record.status)} color={STATUS_COLORS[record.status] || 'default'} />
                    </Box>
                    <Typography color="text.secondary">
                        {kindLabel(translate, record.kind)}
                        {period ? ` · ${period}` : ''}
                        {record.reviewer ? ` · ${translate('Reviewer')} : ${record.reviewer.fullname}` : ''}
                    </Typography>
                </Box>
                <StagePath grid={grid} current={record.stage_current} target={record.stage_target} />
                <ReviewActions />
            </CardContent>
        </Card>
    );
};

// Axis labels colored by block, so the blocks read as sectors of the rose.
const AngleTick = ({ payload, x, y, textAnchor, blockColors, axes }) => {
    const axis = axes.find(item => item.short === payload.value);
    return (
        <text x={x} y={y} textAnchor={textAnchor} fontSize={12} fill={blockColors[axis?.block] || '#666'} dominantBaseline="central">
            {payload.value}
        </text>
    );
};

const ReviewRose = ({ grid, blockColors }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const theme = useTheme();

    const data = grid.axes.map(axis => {
        const entry = record.scores?.[axis.key] || {};
        return {
            name: axis.short,
            fullName: axis.name,
            score: Number.isInteger(entry.score) ? entry.score : 0,
            target: targetFor(grid, record.stage_current, axis.block) ?? 0,
        };
    });
    const hasTarget = data.some(item => item.target > 0);

    return (
        <Card variant="outlined" sx={cardSx}>
            <CardContent>
                <Typography variant="h6">{translate('Profile')}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {translate('Read the shape, not an average: each spoke is one axis, from Learn at the centre to Inspire at the edge.')}
                </Typography>
                <Box sx={{ width: '100%', height: 480 }}>
                    <ResponsiveContainer>
                        <RadarChart data={data} outerRadius="76%">
                            <PolarGrid stroke={alpha(theme.palette.text.primary, 0.12)} />
                            <PolarAngleAxis dataKey="name" tick={<AngleTick blockColors={blockColors} axes={grid.axes} />} />
                            <PolarRadiusAxis domain={[0, 4]} tickCount={5} angle={90} tick={{ fontSize: 10 }} axisLine={false} />
                            {hasTarget && (
                                <Radar
                                    name={translate('Stage target')}
                                    dataKey="target"
                                    stroke={theme.palette.text.secondary}
                                    strokeDasharray="5 4"
                                    fill="none"
                                    isAnimationActive={false}
                                />
                            )}
                            <Radar
                                name={translate('Score')}
                                dataKey="score"
                                stroke={theme.palette.primary.main}
                                fill={theme.palette.primary.main}
                                fillOpacity={0.25}
                                dot={{ r: 3 }}
                            />
                            <Tooltip
                                formatter={(value) => value || '-'}
                                labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                            />
                            <Legend />
                        </RadarChart>
                    </ResponsiveContainer>
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                    {grid.blocks.map(block => (
                        <Box key={block.key} display="flex" alignItems="center" gap={0.75}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: blockColors[block.key] }} />
                            <Typography variant="caption">{block.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};

const AxisChips = ({ title, axes, color }) => axes.length > 0 && (
    <Box sx={{ marginTop: '20px' }}>
        <Typography variant="subtitle2" gutterBottom>{title}</Typography>
        <Box display="flex" gap={0.75} flexWrap="wrap">
            {axes.map(axis => <Pill key={axis.key} variant="outlined" color={color} label={axis.name} />)}
        </Box>
    </Box>
);

// Counts, never an average: how many axes sit above, on or below the stage target.
const Overview = ({ grid }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const theme = useTheme();
    const levelColors = useLevelColors();

    const counts = { above: 0, on: 0, below: 0, unscored: 0 };
    const aboveAxes = [];
    const belowAxes = [];
    const levels = { 1: 0, 2: 0, 3: 0, 4: 0 };
    grid.axes.forEach(axis => {
        const entry = record.scores?.[axis.key] || {};
        const target = targetFor(grid, record.stage_current, axis.block);
        if (!Number.isInteger(entry.score)) {
            counts.unscored += 1;
            return;
        }
        levels[entry.score] += 1;
        if (target === null) {
            return;
        }
        if (entry.score > target) {
            counts.above += 1;
            aboveAxes.push(axis);
        } else if (entry.score === target) {
            counts.on += 1;
        } else {
            counts.below += 1;
            belowAxes.push(axis);
        }
    });

    const tiles = [
        { key: 'above', label: translate('Above target'), color: theme.palette.success.main },
        { key: 'on', label: translate('On target'), color: theme.palette.text.secondary },
        { key: 'below', label: translate('Below target'), color: theme.palette.error.main },
        { key: 'unscored', label: translate('Not scored'), color: theme.palette.text.disabled },
    ];
    const scored = Object.values(levels).reduce((sum, value) => sum + value, 0);

    return (
        <Card variant="outlined" sx={cardSx}>
            <CardContent>
                <Typography variant="h6" gutterBottom>{translate('At a glance')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5, marginBottom: '24px' }}>
                    {tiles.map(tile => (
                        <Box key={tile.key} sx={{ padding: '12px', borderRadius: '10px', border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="h4" sx={{ color: tile.color, fontWeight: 600, lineHeight: 1.1 }}>{counts[tile.key]}</Typography>
                            <Typography variant="body2" color="text.secondary">{tile.label}</Typography>
                        </Box>
                    ))}
                </Box>
                {!record.stage_current && (
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ marginTop: '-12px', marginBottom: '16px' }}>
                        {translate('No current stage: no target to compare with.')}
                    </Typography>
                )}

                <Typography variant="subtitle2" gutterBottom>{translate('Spread across levels')}</Typography>
                <Box sx={{ display: 'flex', height: 14, borderRadius: '7px', overflow: 'hidden', backgroundColor: 'action.hover', marginBottom: '8px' }}>
                    {scored > 0 && grid.levels.filter(level => levels[level.value] > 0).map(level => (
                        <MuiTooltip key={level.value} title={`${level.value} · ${level.label} : ${levels[level.value]}`}>
                            <Box sx={{ width: `${(levels[level.value] / scored) * 100}%`, backgroundColor: levelColors[level.value] }} />
                        </MuiTooltip>
                    ))}
                </Box>
                <Box display="flex" gap={2} flexWrap="wrap">
                    {grid.levels.map(level => (
                        <Box key={level.value} display="flex" alignItems="center" gap={0.75}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: levelColors[level.value] }} />
                            <Typography variant="caption">{level.value} · {level.label} ({levels[level.value]})</Typography>
                        </Box>
                    ))}
                </Box>
                <AxisChips title={translate('Above the stage target')} axes={aboveAxes} color="success" />
                <AxisChips title={translate('Below the stage target')} axes={belowAxes} color="error" />
            </CardContent>
        </Card>
    );
};

// Four steps, the reached ones filled with the level color, the stage target ringed.
const LevelScale = ({ score, target, na }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const levelColors = useLevelColors();

    if (na) {
        return <Typography variant="body2" color="text.secondary">{translate('Not applicable')}</Typography>;
    }

    return (
        <Box display="flex" gap={0.5} alignItems="center">
            {[1, 2, 3, 4].map(step => {
                const reached = Number.isInteger(score) && step <= score;
                return (
                    <Box
                        key={step}
                        sx={{
                            width: 26,
                            height: 10,
                            borderRadius: '5px',
                            backgroundColor: reached ? levelColors[score] : alpha(theme.palette.text.primary, 0.08),
                            outline: step === target ? `2px solid ${theme.palette.text.secondary}` : 'none',
                            outlineOffset: '2px',
                        }}
                    />
                );
            })}
        </Box>
    );
};

const AxisRow = ({ grid, axis, entry, target }) => {
    const translate = useTranslate();
    const levelColors = useLevelColors();
    const score = entry.score;

    return (
        <Box sx={{ padding: '14px 0', borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) 150px 150px 60px' }, gap: 2, alignItems: 'center' }}>
                <Box>
                    <Typography sx={{ fontWeight: 600 }}>{axis.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{axis.question}</Typography>
                </Box>
                <LevelScale score={score} target={target} na={entry.na} />
                <Box>
                    {Number.isInteger(score) && (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: levelColors[score] }}>
                            {score} · {levelLabel(grid, score)}
                        </Typography>
                    )}
                    {!Number.isInteger(score) && !entry.na && (
                        <Typography variant="body2" color="text.disabled">{translate('Not observed')}</Typography>
                    )}
                    {target !== null && (
                        <Typography variant="caption" color="text.secondary">{translate('Target')} {target}</Typography>
                    )}
                </Box>
                <Box sx={{ justifySelf: { md: 'end' } }}><GapChip score={score} target={target} /></Box>
            </Box>
            {Number.isInteger(score) && (
                <Typography variant="body2" color="text.secondary" sx={{ marginTop: '6px', fontStyle: 'italic' }}>
                    {axis.levels[score - 1]}
                </Typography>
            )}
            {entry.fact && (
                <Box sx={{ marginTop: '8px', padding: '8px 12px', borderLeft: '3px solid', borderColor: 'primary.main', backgroundColor: 'action.hover', borderRadius: '0 6px 6px 0' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{entry.fact}</Typography>
                </Box>
            )}
        </Box>
    );
};

const TextCard = ({ title, value, color }) => (
    <Card variant="outlined" sx={{ ...cardSx, borderTop: '4px solid', borderTopColor: color }}>
        <CardContent>
            <Typography variant="h6" gutterBottom>{title}</Typography>
            {value
                ? <Typography sx={{ whiteSpace: 'pre-line' }}>{value}</Typography>
                : <Typography color="text.disabled">-</Typography>}
        </CardContent>
    </Card>
);

// Written by the person reviewed, read by the manager. Edited in place, without leaving the page.
const ActionPlanCard = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const canEdit = record.actions?.edit_action_plan;

    const startEditing = () => {
        setText(record.action_plan || '');
        setEditing(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            await putReviewActionPlan(record.id, text);
            setEditing(false);
            refresh();
        } catch (error) {
            notify(error.message, { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card variant="outlined" sx={{ borderRadius: '12px', borderTop: '4px solid', borderTopColor: 'secondary.main', gridColumn: { md: '1 / -1' } }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <Box>
                        <Typography variant="h6">{translate('Action plan')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {translate('What the person will put in place to reach the objectives or improve the situation.')}
                        </Typography>
                    </Box>
                    {canEdit && !editing && (
                        <Button variant="outlined" onClick={startEditing}>
                            {record.action_plan ? translate('Edit my action plan') : translate('Write my action plan')}
                        </Button>
                    )}
                </Box>
                {editing ? (
                    <Box sx={{ marginTop: '16px' }}>
                        <TextField
                            multiline
                            minRows={5}
                            fullWidth
                            autoFocus
                            value={text}
                            onChange={(event) => setText(event.target.value)}
                            placeholder={translate('Concrete actions, with a date when possible.')}
                        />
                        <Box display="flex" gap={1} justifyContent="flex-end" sx={{ marginTop: '8px' }}>
                            <Button onClick={() => setEditing(false)} disabled={saving}>{translate('ra.action.cancel')}</Button>
                            <Button variant="contained" onClick={save} disabled={saving}>{translate('ra.action.save')}</Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ marginTop: '12px' }}>
                        {record.action_plan
                            ? <Typography sx={{ whiteSpace: 'pre-line' }}>{record.action_plan}</Typography>
                            : <Typography color="text.disabled">{translate('Not written yet.')}</Typography>}
                        {record.action_plan_updated_at && (
                            <Typography variant="caption" color="text.secondary" component="p" sx={{ marginTop: '8px' }}>
                                {translate('Updated on %{date}', { date: record.action_plan_updated_at.slice(0, 10) })}
                            </Typography>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const ReviewContent = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const theme = useTheme();
    const grid = useReviewGrid();

    if (!record || !grid) {
        return <Loading />;
    }

    const blockPalette = [theme.palette.primary.main, theme.palette.info.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main];
    const blockColors = Object.fromEntries(grid.blocks.map((block, index) => [block.key, blockPalette[index % blockPalette.length]]));

    return (
        <Box sx={{ marginTop: '16px' }}>
            <Header grid={grid} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(0, 2fr)' }, gap: 2, marginBottom: '16px' }}>
                <ReviewRose grid={grid} blockColors={blockColors} />
                <Overview grid={grid} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, marginBottom: '16px' }}>
                <TextCard title={translate('Strengths')} value={record.strengths} color="success.main" />
                <TextCard title={translate('Areas to improve')} value={record.improvements} color="warning.main" />
                <TextCard title={translate('Values embodied')} value={record.values_note} color="info.main" />
                <TextCard title={translate('Objectives (verifiable)')} value={record.objectives} color="primary.main" />
                <ActionPlanCard />
            </Box>

            {axesByBlock(grid).map(block => (
                <Card key={block.key} variant="outlined" sx={{ borderRadius: '12px', marginBottom: '16px', borderLeft: '4px solid', borderLeftColor: blockColors[block.key] }}>
                    <CardContent>
                        <Typography variant="h6">{block.label}</Typography>
                        {block.axes.map(axis => (
                            <AxisRow
                                key={axis.key}
                                grid={grid}
                                axis={axis}
                                entry={record.scores?.[axis.key] || {}}
                                target={targetFor(grid, record.stage_current, axis.block)}
                            />
                        ))}
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
};

const ReviewShow = (props) => (
    <ShowBase {...props}>
        <ReviewContent />
    </ShowBase>
);

export default ReviewShow;
